// src/pages/Login.js
import React, { useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { login as apiLogin, getProfile } from "../services/api";
import { AuthContext } from "../contexts/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AuthContext) || {}; // may or may not expose login()

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!username || !password) {
      setError("Please enter username and password.");
      return;
    }
    setLoading(true);
    try {
      // 1) Login (server returns { token, user })
      const res = await apiLogin(username, password);
      if (!res || !res.token) throw new Error("Invalid response from server");

      // 2) Persist token immediately
      localStorage.setItem("token", res.token);

      // 3) Get user (from response or profile endpoint)
      let userObj = res.user;
      if (!userObj) {
        try {
          userObj = await getProfile();
        } catch {
          /* ignore if backend doesn't provide profile */
        }
      }

      // 4) If AuthContext exposes a login() method, use it; else rely on localStorage bootstrap
      if (typeof auth.login === "function") {
        auth.login(userObj || null, res.token);
      }

      // 5) Navigate AFTER we’ve set auth (prevents bounce back to /login)
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
    } catch (err) {
      // Clear bad token if any
      localStorage.removeItem("token");
      setError(typeof err === "string" ? err : err?.message || "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <form onSubmit={handleSubmit} className="w-full max-w-sm bg-white p-6 rounded shadow">
        <h1 className="text-2xl font-bold mb-4">Sign in</h1>

        {error && (
          <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 p-2 rounded">
            {error}
          </div>
        )}

        <label className="block mb-2">
          <span className="text-sm text-gray-700">Username</span>
          <input
            className="mt-1 block w-full border rounded p-2"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="e.g. admin@acme.com"
            autoComplete="username"
          />
        </label>

        <label className="block mb-4">
          <span className="text-sm text-gray-700">Password</span>
          <input
            type="password"
            className="mt-1 block w-full border rounded p-2"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
